import React, { useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Tag, Info, Copy, ArrowLeftRight, Calendar, Package, CheckCircle2, ChevronRight
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { CONTAINER_GROUPS, COMMODITIES, IMO_CLASSES, RECENT_QUOTES_STORAGE_KEY } from '../lib/quoteOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().slice(0, 10);

const inputCls = 'h-12 w-full px-4 rounded-md border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition text-[15px]';
const labelCls = 'block text-sm font-medium mb-2 text-[color:var(--ink)]';

function readRecentQuotes() {
  try {
    const raw = localStorage.getItem(RECENT_QUOTES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentQuote(q) {
  try {
    const list = readRecentQuotes();
    list.unshift(q);
    localStorage.setItem(RECENT_QUOTES_STORAGE_KEY, JSON.stringify(list.slice(0, 20)));
  } catch { /* localStorage unavailable - ignore */ }
}

const emptyForm = {
  startLocation: '', startType: 'terminal',
  endLocation: '', endType: 'terminal',
  validFrom: todayISO(),
  soc: false,
  containerType: CONTAINER_GROUPS[0].options[0],
  containerQty: 1,
  weightPerContainer: 0,
  weightUnit: 'kg',
  dangerousGoods: false,
  imoClass: '',
  unNumber: '',
  commodity: COMMODITIES[0],
  companyName: '', name: '', email: '', phone: '',
};

function DoorTerminalToggle({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-5 mt-2">
      <span className="text-sm text-[color:var(--muted)]">{label}</span>
      {[['door', 'Your Door'], ['terminal', 'Terminal/Ramp']].map(([val, text]) => (
        <label key={val} className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="radio"
            checked={value === val}
            onChange={() => onChange(val)}
            className="accent-[color:var(--brand)] w-4 h-4"
          />
          {text}
        </label>
      ))}
    </div>
  );
}

function NewQuoteForm() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const swap = () => setForm((f) => ({
    ...f,
    startLocation: f.endLocation, startType: f.endType,
    endLocation: f.startLocation, endType: f.startType,
  }));

  const duplicate = () => {
    const recent = readRecentQuotes();
    if (!recent.length) {
      toast({ title: 'Nothing to duplicate yet', description: 'Submit a quote first, then duplicate it.' });
      return;
    }
    const last = recent[0];
    setForm((f) => ({
      ...f,
      startLocation: last.startLocation, startType: last.startType,
      endLocation: last.endLocation, endType: last.endType,
      containerType: last.containerType, containerQty: last.containerQty,
      weightPerContainer: last.weightPerContainer, weightUnit: last.weightUnit,
      commodity: last.commodity, soc: last.soc, dangerousGoods: last.dangerousGoods,
      imoClass: last.imoClass || '', unNumber: last.unNumber || '',
    }));
    toast({ title: 'Routing duplicated', description: 'Details copied from your last quote.' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.startLocation || !form.endLocation || !form.name || !form.email) {
      toast({ title: 'Missing fields', description: 'Please fill start/end location, name and email.' });
      return;
    }
    if (!form.weightPerContainer || Number(form.weightPerContainer) <= 0) {
      toast({ title: 'Missing weight', description: 'Please enter the weight per container (must be greater than 0).' });
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/quote`, {
        ...form,
        containerQty: Number(form.containerQty),
        weightPerContainer: Number(form.weightPerContainer),
      });
      setResult(r.data);
      saveRecentQuote(r.data);
      toast({ variant: 'success', title: 'Quote request sent', description: `Reference ${r.data.quoteRef} — our team will follow up shortly.` });
    } catch (err) {
      // Surface the real reason (validation error, network/CORS failure, etc.) instead of a generic message,
      // so it's obvious in testing why a request didn't save.
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
        : (detail || err?.message || 'Please try again.');
      console.error('Quote submit failed:', err);
      toast({ title: 'Could not submit quote', description: msg });
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="rounded-md border border-[color:var(--brand)]/30 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2 text-[color:var(--brand)] mb-4">
          <CheckCircle2 size={20} /> <span className="text-xs uppercase tracking-widest font-semibold">Quote Requested</span>
        </div>
        <h3 className="text-2xl font-semibold text-[color:var(--ink)] mb-2">Thank you, {result.name.split(' ')[0]}!</h3>
        <p className="text-sm text-[color:var(--muted)] mb-6">
          We've received your quote request. Our team will get back to you with a firm, bookable rate within one business day.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 text-sm mb-6">
          <div className="p-3 rounded bg-[color:var(--brand-soft)]/30 border border-[color:var(--line)]">
            <div className="text-xs text-[color:var(--muted)] uppercase tracking-wide mb-1">Reference</div>
            <div className="font-semibold">{result.quoteRef}</div>
          </div>
          <div className="p-3 rounded bg-[color:var(--brand-soft)]/30 border border-[color:var(--line)]">
            <div className="text-xs text-[color:var(--muted)] uppercase tracking-wide mb-1">Route</div>
            <div className="font-semibold">{result.startLocation} → {result.endLocation}</div>
          </div>
          <div className="p-3 rounded bg-[color:var(--brand-soft)]/30 border border-[color:var(--line)]">
            <div className="text-xs text-[color:var(--muted)] uppercase tracking-wide mb-1">Container</div>
            <div className="font-semibold">{result.containerQty} × {result.containerType}</div>
          </div>
          <div className="p-3 rounded bg-[color:var(--brand-soft)]/30 border border-[color:var(--line)]">
            <div className="text-xs text-[color:var(--muted)] uppercase tracking-wide mb-1">Commodity</div>
            <div className="font-semibold">{result.commodity}</div>
          </div>
        </div>
        <button
          onClick={() => { setResult(null); setForm(emptyForm); }}
          className="h-11 px-5 rounded-md border border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand-soft)]/30 transition-colors text-sm font-medium"
        >
          Start a new quote
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      {/* Routing */}
      <section className="bg-white rounded-md border border-[color:var(--line)] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="w-7 h-7 rounded-full border border-[color:var(--line)] flex items-center justify-center text-xs font-semibold text-[color:var(--muted)]">1</span>
            <h3 className="text-lg font-semibold text-[color:var(--ink)]">Routing</h3>
          </div>
          <button type="button" onClick={duplicate} className="flex items-center gap-1.5 text-sm font-medium text-[color:var(--brand)] hover:underline">
            <Copy size={15} /> Duplicate
          </button>
        </div>

        <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
          <div>
            <label className={labelCls}>Start Location</label>
            <input required placeholder="City, port or inland location" value={form.startLocation} onChange={set('startLocation')} className={inputCls} />
            <DoorTerminalToggle label="Received at" value={form.startType} onChange={(v) => set('startType')(v)} />
          </div>
          <button type="button" onClick={swap} aria-label="Swap locations"
            className="hidden md:flex mt-9 w-10 h-10 rounded-md bg-[color:var(--ink)] text-white items-center justify-center hover:bg-[color:var(--brand)] transition-colors shrink-0">
            <ArrowLeftRight size={16} />
          </button>
          <div>
            <label className={labelCls}>End Location</label>
            <input required placeholder="City, port or inland location" value={form.endLocation} onChange={set('endLocation')} className={inputCls} />
            <DoorTerminalToggle label="Delivered to" value={form.endType} onChange={(v) => set('endType')(v)} />
          </div>
        </div>
      </section>

      {/* Validity Date */}
      <section className="bg-white rounded-md border border-[color:var(--line)] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Calendar size={18} className="text-[color:var(--brand)]" />
          <h3 className="text-lg font-semibold text-[color:var(--ink)]">Validity Date</h3>
        </div>
        <div className="max-w-xs">
          <label className={labelCls}>Valid from</label>
          <input required type="date" min={todayISO()} value={form.validFrom} onChange={set('validFrom')} className={inputCls} />
        </div>
      </section>

      {/* Container and Commodity Details */}
      <section className="bg-white rounded-md border border-[color:var(--line)] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Package size={18} className="text-[color:var(--brand)]" />
          <h3 className="text-lg font-semibold text-[color:var(--ink)]">Container and Commodity Details</h3>
        </div>

        <label className="flex items-center gap-2 text-sm mb-5 cursor-pointer select-none">
          <input type="checkbox" checked={form.soc} onChange={set('soc')} className="w-4 h-4 accent-[color:var(--brand)]" />
          Shipper Owned Container (SOC)
          <Info size={14} className="text-[color:var(--muted)]" />
        </label>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Container Type</label>
            <select value={form.containerType} onChange={set('containerType')} className={inputCls}>
              {CONTAINER_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label.toUpperCase()}>
                  {g.options.map((o) => (
                    <option key={o} value={o} disabled={g.socOnly && !form.soc}>{o}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Container Quantity</label>
            <input required type="number" min="1" max="999" value={form.containerQty} onChange={set('containerQty')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Weight per Container</label>
            <div className="flex gap-3 items-center">
              <input required type="number" min="1" value={form.weightPerContainer} onChange={set('weightPerContainer')} className={inputCls} />
              <div className="flex items-center gap-3 shrink-0 text-sm">
                {['kg', 'lb'].map((u) => (
                  <label key={u} className="flex items-center gap-1 cursor-pointer select-none">
                    <input type="radio" checked={form.weightUnit === u} onChange={() => set('weightUnit')(u)} className="accent-[color:var(--brand)] w-4 h-4" />
                    {u}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-3 p-4 rounded-md bg-[color:var(--brand-soft)]/30 border border-[color:var(--brand)]/15">
          <Info size={18} className="text-[color:var(--brand)] mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-[color:var(--ink)] mb-0.5">Multiple Container Types</div>
            <p className="text-[color:var(--muted)]">
              You will get a quotation offer that has both the selected container type and others from the group: 20'STD, 40'STD and 40'HC — on one document.
            </p>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm mt-5 cursor-pointer select-none">
          <input type="checkbox" checked={form.dangerousGoods} onChange={set('dangerousGoods')} className="w-4 h-4 accent-[color:var(--brand)]" />
          Dangerous Goods
        </label>

        {form.dangerousGoods && (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>IMO Class (Optional)</label>
              <select value={form.imoClass} onChange={set('imoClass')} className={inputCls}>
                <option value="">Select IMO class</option>
                {IMO_CLASSES.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map((o) => (
                      <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>UN Number (Optional)</label>
              <input placeholder="e.g. 1263" value={form.unNumber} onChange={set('unNumber')} className={inputCls} />
            </div>
          </div>
        )}

        <div className="mt-5 max-w-md">
          <label className={labelCls}>Commodity</label>
          <select value={form.commodity} onChange={set('commodity')} className={inputCls}>
            {COMMODITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-white rounded-md border border-[color:var(--line)] p-6 md:p-8">
        <p className="text-sm text-[color:var(--muted)] mb-5">
          Tell us who to send this indicative, bookable rate to — our team will confirm final pricing by email.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <input placeholder="Company Name" value={form.companyName} onChange={set('companyName')} className={inputCls} />
          <input required placeholder="Your Name *" value={form.name} onChange={set('name')} className={inputCls} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <input required type="email" placeholder="Email *" value={form.email} onChange={set('email')} className={inputCls} />
          <input placeholder="Phone" value={form.phone} onChange={set('phone')} className={inputCls} />
        </div>
      </section>

      <button disabled={loading} type="submit"
        className="h-12 px-8 rounded-md bg-[color:var(--brand)] text-white flex items-center gap-2 hover:bg-[color:var(--brand-2)] transition-colors disabled:opacity-60 font-medium">
        {loading ? 'Submitting…' : 'Get Quote'} <ChevronRight size={16} />
      </button>
    </form>
  );
}

export default function QuotePage() {
  return (
    <>
      <Header />
      <div className="page-hero" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1600&auto=format&fit=crop)', paddingTop: '80px' }}>
        <div className="text-center max-w-3xl mx-auto px-6">
          <p className="uppercase text-xs tracking-[0.4em] mb-3 opacity-80">Instant Estimate</p>
          <h1 className="text-4xl md:text-6xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>Request a Quote</h1>
          <div className="mx-auto w-16 h-[2px] bg-white/70 mt-5" />
        </div>
      </div>
      <div className="bg-[color:var(--brand-soft)]/10 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 rounded-md bg-[color:var(--brand-soft)]/40 flex items-center justify-center text-[color:var(--brand)]">
              <Tag size={20} />
            </span>
            <h1 className="text-3xl font-semibold text-[color:var(--ink)]">New Quote</h1>
          </div>
          <NewQuoteForm />
        </div>
      </div>
      <Footer />
    </>
  );
}
